import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { adminApi, adminStore } from '../../adminApi';
import { IconActivity, IconShield, IconUsers, IconCreditCard, IconGem, IconBell, IconServer, IconUpload, IconMail, IconFileText, IconBot, IconArchive, IconSettings, IconUser, IconLogOut, IconX, IconSend, IconArrowLeft, IconRefresh } from '../../components/Icon';
import { LOGOS } from '../../cdn';

function renderMd(md: string): string {
  try {
    const raw = marked.parse(md, { gfm: true, breaks: true }) as string;
    return DOMPurify.sanitize(raw, { ADD_TAGS: ['form','input','button'], ADD_ATTR: ['method','action','style','value','type','name','onsubmit'] });
  } catch { return md.replace(/</g,'&lt;'); }
}

const GROUPS: { title: string; items: { to: string; label: string; Icon: any; end?: boolean }[] }[] = [
  { title: 'Overview', items: [
    { to: '/admin', label: 'Dashboard', Icon: IconActivity, end: true },
    { to: '/admin/traffic', label: 'Traffic', Icon: IconActivity },
    { to: '/admin/security', label: 'Security', Icon: IconShield },
  ]},
  { title: 'Growth', items: [
    { to: '/admin/users', label: 'Users', Icon: IconUsers },
    { to: '/admin/payments', label: 'Payments', Icon: IconCreditCard },
    { to: '/admin/refunds', label: 'Refunds', Icon: IconRefresh },
    { to: '/admin/coupons', label: 'Coupons', Icon: IconGem },
    { to: '/admin/alerts', label: 'Alerts', Icon: IconBell },
  ]},
  { title: 'Ops', items: [
    { to: '/admin/servers', label: 'Servers', Icon: IconServer },
    { to: '/admin/apps', label: 'Apps', Icon: IconUpload },
    { to: '/admin/email', label: 'Email', Icon: IconMail },
    { to: '/admin/contacts', label: 'Inbox', Icon: IconMail },
    { to: '/admin/submissions', label: 'Reviews', Icon: IconFileText },
  ]},
  { title: 'System', items: [
    { to: '/admin/logs', label: 'Logs', Icon: IconFileText },
    { to: '/admin/ai', label: 'AI Chat', Icon: IconBot },
    { to: '/admin/trash', label: 'Trash', Icon: IconArchive },
    { to: '/admin/settings', label: 'Settings', Icon: IconSettings },
  ]},
];

export default function AdminLayout() {
  const nav = useNavigate();
  const loc = useLocation();
  const [aiOpen, setAiOpen] = useState(false);
  const [aiInput, setAiInput] = useState('');
  const [aiMsgs, setAiMsgs] = useState<{role:'user'|'assistant',content:string}[]>([]);
  const [aiBusy, setAiBusy] = useState(false);

  // Silent session check on every route change — no blocking loading screen.
  useEffect(() => {
    if (!adminStore.loggedIn()) { nav('/admin/login', { replace: true }); return; }
    adminApi.me().then(r => {
      if (r.status !== 200) { adminStore.clear(); nav('/admin/login', { replace: true }); }
    }).catch(() => {});
  }, [loc.pathname]);

  // Keep-alive heartbeat — every 4 min ping /admin/me so the backend's
  // sliding-renewal window fires and the admin doesn't get kicked while
  // idling on a single page. Pauses while the tab is hidden; fires once
  // on visibility restore so a tab returned to after lunch stays logged in.
  useEffect(() => {
    if (!adminStore.loggedIn()) return;
    let stopped = false;
    const ping = () => {
      if (stopped || document.hidden || !adminStore.loggedIn()) return;
      adminApi.me().then(r => {
        if (r.status === 401) { adminStore.clear(); nav('/admin/login', { replace: true }); }
      }).catch(() => {});
    };
    const id = window.setInterval(ping, 4 * 60 * 1000);
    const onVis = () => { if (!document.hidden) ping(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { stopped = true; window.clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, []);

  const sendAi = async () => {
    const m = aiInput.trim(); if (!m || aiBusy) return;
    setAiInput(''); setAiMsgs(s=>[...s,{role:'user',content:m}]); setAiBusy(true);
    const r = await adminApi.aiChat(m, aiMsgs);
    setAiBusy(false);
    setAiMsgs(s=>[...s,{role:'assistant',content: r.status===200 ? (r.data?.reply||JSON.stringify(r.data)) : (r.data?.message||'Failed')}]);
  };

  const NavLink = ({ to, label, Icon, end }: any) => {
    const active = end ? loc.pathname === to : loc.pathname === to || loc.pathname.startsWith(to + '/');
    return (
      <Link to={to} className={`flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition group ${active ? 'bg-gradient-to-r from-[color:var(--color-brand)]/20 to-[color:var(--color-brand-deep)]/20 text-[color:var(--color-brand-glow)] border border-[color:var(--color-brand)]/30 shadow-[0_4px_20px_rgba(232,71,106,0.15)]' : 'text-[color:var(--color-ink-dim)] hover:bg-white/[0.04] hover:text-white border border-transparent'}`}>
        <span className={`w-7 h-7 rounded-lg grid place-items-center ${active ? 'bg-[color:var(--color-brand)]/20 text-[color:var(--color-brand-glow)]' : 'bg-white/[0.04] group-hover:bg-white/[0.08]'}`}><Icon width={14} height={14}/></span>
        <span className="font-medium">{label}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] text-[color:var(--color-ink)] flex">
      {/* ambient orbs — frontend manner */}
      <div aria-hidden className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[color:var(--color-brand)]/12 blur-[120px]" />
        <div className="absolute top-1/3 -left-40 w-[500px] h-[500px] rounded-full bg-[color:var(--color-brand-deep)]/10 blur-[120px]" />
      </div>

      {/* sidebar — glass, sticky */}
      <aside className="hidden lg:flex w-[260px] shrink-0 flex-col sticky top-0 h-screen z-10">
        <div className="flex-1 flex flex-col m-3 rounded-[20px] border border-[color:var(--color-line)] bg-black/30 backdrop-blur-xl overflow-hidden">
          <div className="px-4 pt-5 pb-3">
            <Link to="/admin" className="flex items-center gap-3 group">
              <span className="w-9 h-9 rounded-xl overflow-hidden bg-[color:var(--color-bg)] ring-1 ring-[color:var(--color-brand)]/30 shadow-lg shadow-[color:var(--color-brand)]/20 grid place-items-center shrink-0">
                <img src={LOGOS.vpnee} alt="Vpnee" width={36} height={36} className="w-full h-full object-cover" />
              </span>
              <div>
                <div className="font-[family-name:var(--font-display)] font-extrabold leading-none">Vpnee Admin</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--color-ink-faint)]">Admin Panel</div>
              </div>
            </Link>
            <div className="mt-3 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[color:var(--color-brand)]/10 border border-[color:var(--color-brand)]/20">
              <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--color-brand-glow)] animate-pulse" />
              <span className="text-[10px] tracking-widest uppercase text-[color:var(--color-brand-glow)] font-bold">Live — HMAC + MFA</span>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-2 space-y-5">
            {GROUPS.map(g => (
              <div key={g.title}>
                <div className="px-3 mb-2 text-[10px] uppercase tracking-[0.16em] text-[color:var(--color-ink-faint)] font-bold">{g.title}</div>
                <div className="space-y-1">{g.items.map(it => <NavLink key={it.to} {...it} />)}</div>
              </div>
            ))}
          </div>

          <div className="p-3 border-t border-[color:var(--color-line)] bg-black/20">
            <div className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white/[0.03] border border-white/[0.06]">
              <div className="w-8 h-8 rounded-lg grid place-items-center bg-gradient-to-br from-[color:var(--color-brand)]/30 to-[color:var(--color-brand-deep)]/30 text-[color:var(--color-brand-glow)]"><IconUser width={16} height={16}/></div>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{adminStore.user()?.username || 'admin'}</div>
                <div className="text-[10px] text-[color:var(--color-ink-faint)]">Administrator</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <button onClick={() => { adminApi.logout(); nav('/admin/login', { replace: true }); }} className="px-3 py-2 rounded-xl text-xs bg-white/[0.04] hover:bg-white/[0.08] border border-[color:var(--color-line)]">Sign out</button>
              <Link to="/" className="px-3 py-2 rounded-xl text-xs bg-[color:var(--color-brand)]/15 hover:bg-[color:var(--color-brand)]/25 border border-[color:var(--color-brand)]/30 text-center text-[color:var(--color-brand-glow)] inline-flex items-center justify-center gap-1.5"><IconArrowLeft width={12} height={12}/> Site</Link>
            </div>
          </div>
        </div>
      </aside>

      {/* main */}
      <div className="flex-1 min-w-0 relative z-10">
        {/* desktop top nav bar — matches frontend Nav (glass, user menu) */}
        <div className="hidden lg:flex sticky top-0 z-20 h-16 items-center gap-4 px-6 border-b border-[color:var(--color-line)] bg-[color:var(--color-bg)]/70 backdrop-blur-xl">
          <span className="text-sm text-[color:var(--color-ink-dim)]">
            {(GROUPS.flatMap(g=>g.items).find(it => it.end ? loc.pathname===it.to : loc.pathname===it.to || loc.pathname.startsWith(it.to+'/'))?.label) || 'Dashboard'}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Link to="/" className="px-3 py-2 rounded-lg text-sm text-[color:var(--color-ink-dim)] hover:text-white hover:bg-white/5 transition inline-flex items-center gap-1.5"><IconArrowLeft width={14} height={14}/> Site</Link>
            <div className="flex items-center gap-2.5 pl-2 pr-1 py-1 rounded-full bg-white/[0.04] border border-[color:var(--color-line)]">
              <span className="w-7 h-7 rounded-full grid place-items-center text-white shrink-0" style={{ background: 'linear-gradient(135deg,var(--color-brand),var(--color-brand-deep))' }}><IconUser width={15} height={15}/></span>
              <span className="text-sm font-semibold max-w-[140px] truncate">{adminStore.user()?.username || 'admin'}</span>
              <button onClick={() => { adminApi.logout(); nav('/admin/login', { replace: true }); }} className="w-8 h-8 rounded-full grid place-items-center text-[color:var(--color-ink-dim)] hover:text-white hover:bg-white/5 transition" aria-label="Sign out"><IconLogOut width={15} height={15}/></button>
            </div>
          </div>
        </div>

        {/* mobile bar — glass */}
        <div className="lg:hidden sticky top-0 z-20 border-b border-[color:var(--color-line)] bg-[color:var(--color-bg)]/70 backdrop-blur-xl">
          <div className="flex items-center gap-2 px-3 py-3">
            <span className="w-7 h-7 rounded-lg overflow-hidden bg-[color:var(--color-bg)] ring-1 ring-[color:var(--color-brand)]/30 grid place-items-center shrink-0"><img src={LOGOS.vpnee} alt="Vpnee" width={28} height={28} className="w-full h-full object-cover" /></span>
            <span className="font-[family-name:var(--font-display)] font-extrabold">Admin</span>
            <span className="text-xs text-[color:var(--color-ink-faint)]">TNIDU</span>
            <div className="ml-auto flex gap-1 overflow-x-auto scrollbar-none">
              {GROUPS.flatMap(g=>g.items).slice(0,6).map(n => (
                <Link key={n.to} to={n.to} className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap border ${loc.pathname.startsWith(n.to) ? 'bg-[color:var(--color-brand)] text-white border-[color:var(--color-brand)]' : 'bg-white/5 border-white/10'}`}>{n.label}</Link>
              ))}
            </div>
          </div>
          <div className="flex gap-1 overflow-x-auto px-3 pb-3">
            {GROUPS.flatMap(g=>g.items).slice(6).map(n => (
              <Link key={n.to} to={n.to} className="px-3 py-1.5 rounded-full text-xs whitespace-nowrap bg-white/5 border border-white/10">{n.label}</Link>
            ))}
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
          <Outlet />
        </motion.div>

        {/* AI assistant FAB — labelled on desktop, icon-only on mobile */}
        <button onClick={()=>setAiOpen(v=>!v)} className="fixed bottom-5 right-5 z-40 h-14 px-4 rounded-full inline-flex items-center gap-2 shadow-[0_12px_30px_rgba(232,71,106,0.35)] border border-[color:var(--color-brand)]/30 text-white font-semibold" style={{background:'linear-gradient(135deg,var(--color-brand),var(--color-brand-deep))'}} aria-label="Open AI assistant">
          <IconBot width={20} height={20}/>
          <span className="hidden sm:inline text-sm">Ask AI</span>
        </button>

        {/* AI drawer */}
        {aiOpen && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={()=>setAiOpen(false)} />
        )}
        <motion.div
          initial={false}
          animate={{x: aiOpen?'0%':'100%'}}
          transition={{type:'spring',stiffness:320,damping:34}}
          className="fixed top-0 right-0 z-50 w-full sm:w-[420px] h-[100dvh] bg-[color:var(--color-surface)] border-l border-[color:var(--color-line)] flex flex-col shadow-2xl"
          role="dialog" aria-label="AI assistant"
        >
          <div className="p-4 border-b border-[color:var(--color-line)] flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="w-9 h-9 rounded-xl grid place-items-center text-white shrink-0" style={{background:'linear-gradient(135deg,var(--color-brand),var(--color-brand-deep))'}}><IconBot width={16} height={16}/></span>
              <div className="min-w-0">
                <div className="font-bold text-sm">AI Assistant</div>
                <div className="text-xs text-[color:var(--color-ink-faint)] flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[color:var(--color-ok)] animate-pulse"/>Live data · users, revenue, bans</div>
              </div>
            </div>
            <button onClick={()=>setAiOpen(false)} className="w-9 h-9 rounded-xl grid place-items-center bg-white/5 hover:bg-white/10 shrink-0" aria-label="Close"><IconX width={16} height={16} /></button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {aiMsgs.length===0 && (
              <div className="py-4">
                <div className="text-sm text-[color:var(--color-ink-dim)] mb-3">Hi 👋 Ask me anything about your panel — I read live stats. Tap a question to start:</div>
                <div className="grid gap-2">
                  {['Who are the top data consumers?','How many payments are pending?','How do I add a new server?','Summarize recent security events','Tips for reviewing a ban appeal'].map(q=>(
                    <button key={q} onClick={()=>{setAiInput(q); setTimeout(sendAi,50);}} className="text-left px-3.5 py-2.5 rounded-xl text-sm bg-white/[0.04] border border-[color:var(--color-line)] hover:border-[color:var(--color-brand)]/40 hover:bg-white/[0.07] transition">{q}</button>
                  ))}
                </div>
              </div>
            )}
            {aiMsgs.map((m,i)=> m.role==='user' ? <div key={i} className="max-w-[88%] ml-auto px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap bg-gradient-to-r from-[color:var(--color-brand)] to-[color:var(--color-brand-deep)] text-white rounded-br-sm">{m.content}</div> : <div key={i} className="max-w-[88%] mr-auto px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed bg-white/[0.06] border border-white/10 rounded-bl-sm prose prose-invert prose-sm max-w-none prose-p:my-1 prose-a:text-[color:var(--color-brand-glow)]" dangerouslySetInnerHTML={{__html: renderMd(m.content)}} />)}
            {aiBusy && <div className="text-xs text-[color:var(--color-ink-faint)] flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[color:var(--color-brand)] animate-pulse"/>AI thinking…</div>}
          </div>

          <div className="p-3 border-t border-[color:var(--color-line)] flex gap-2 pb-[calc(env(safe-area-inset-bottom,0px)+0.75rem)]">
            <input value={aiInput} onChange={e=>setAiInput(e.target.value)} onKeyDown={e=>e.key==='Enter'&&sendAi()} placeholder="Type your question…" className="flex-1 min-w-0 rounded-xl bg-black/30 border border-[color:var(--color-line)] px-3.5 py-3 text-sm outline-none focus:border-[color:var(--color-brand)]" />
            <button disabled={aiBusy||!aiInput.trim()} onClick={sendAi} className="btn btn-primary !px-4 shrink-0 disabled:opacity-40 inline-flex items-center gap-1.5" aria-label="Send">Send <IconSend width={14} height={14} /></button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
